import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { photoStore } from './photoStore';
import { useTable, Column, useFlexLayout } from 'react-table';
import { Button } from 'react-bootstrap';
import { TFunction } from 'i18next';
import moment from 'moment';
import { Itile, Iphoto } from '../../models';
import { TableUtil } from '../common/TableUtil';
import CommonModal from '../common/CommonModal';
import type { getPhotos, updatePhoto, deletePhoto } from '../../actions/photosActions';
import EditPhotoForm from './EditPhotoForm';
import { initialPhoto } from '../../reducers/initialState';
import loadingPlaceholder from '../../images/Photo_Loading.png';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import map from 'lodash/map';
import PhotoList from './Mobile/PhotoList';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

interface Iprops {
    installBaseID: string;
    getPhotos: typeof getPhotos;
    updatePhoto: typeof updatePhoto;
    deletePhoto: typeof deletePhoto;
    tableData: Iphoto[];
    t: TFunction;
    loading: boolean;
    currentTile: Itile;
    isMobile: boolean;
}

export const PhotoTable: React.FC<Iprops> = props => {
    const { loading, t, isMobile, tableData, installBaseID, deletePhoto, updatePhoto } = props;
    const [selectedPhoto, setSelectedPhoto] = useState<Iphoto>(initialPhoto);
    const [showEditPhotoModal, setShowEditPhotoModal] = useState<boolean>(false);
    const [activePhoto, setActivePhoto] = useState<number>(0);
    const [lightboxIsOpen, setLightboxIsOpen] = useState<boolean>(false);
    const [tableDataWithFallback, setTableDataWithFallback] = useState<Iphoto[]>(tableData);

    const addLocalImageUrl = (image: Iphoto) => {
        return photoStore.getItem(image.id).then(item => {
            if (!item) return image;
            const localUrl = window.URL.createObjectURL(item);
            return { ...image, lightboxUrl: localUrl, thumbnailUrl: localUrl };
        });
    };

    const handleImageFallback = React.useCallback(() => {
        const imageLocalPromises: Array<Promise<any>> = [];
        forEach(tableData, (image: Iphoto) => {
            if (image.lightboxUrl.startsWith('blob:')) {
                imageLocalPromises.push(addLocalImageUrl(image));
            } else if (image.lightboxUrl.length === 0) {
                // TODO: fallback to default image
            }
        });
        Promise.all(imageLocalPromises).then(localImages => {
            const newTableDataWithFallback = map(tableData, (image: Iphoto) => {
                const foundLocalImage = find(localImages, { id: image.id });
                if (foundLocalImage) {
                    return foundLocalImage;
                } else {
                    return image;
                }
            });
            setTableDataWithFallback(newTableDataWithFallback);
        });
    }, [tableData]);

    const closeLightbox = () => {
        setActivePhoto(0);
        setLightboxIsOpen(false);
    };

    // Helper function to get image source (base64 or URL fallback)
    const getThumbnailSrc = (photo: Iphoto) => {
        if (photo.thumbnailImage) {
            return `data:image/jpeg;base64,${photo.thumbnailImage}`;
        }
        return photo.thumbnailUrl || loadingPlaceholder;
    };

    const getLightboxSrc = (photo: Iphoto) => {
        if (photo.lightboxImage) {
            return `data:image/jpeg;base64,${photo.lightboxImage}`;
        }
        return photo.lightboxUrl || photo.thumbnailUrl || loadingPlaceholder;
    };

    // Build columns using useMemo for performance
    const columns = useMemo<Column<Iphoto>[]>(() => {
        return TableUtil.translateHeaders(
            [
                {
                    Header: 'photoHeader',
                    accessor: 'thumbnailUrl',
                    width: 160,
                    Cell: ({ value, row }: { value: any; row: any }) => (
                        <img
                            style={{
                                maxWidth: '140px',
                                cursor: 'pointer'
                            }}
                            src={getThumbnailSrc(row.original)}
                            alt=""
                            onClick={() => {
                                setActivePhoto(row.index);
                                setLightboxIsOpen(true);
                            }}
                        />
                    )
                },
                {
                    Header: 'commentsHeader',
                    accessor: 'comments',
                    minWidth: 80,
                    width: 200
                },
                {
                    Header: 'dateHeader',
                    accessor: 'createDate',
                    width: 110,
                    Cell: ({ value }: { value: any }) => <span>{moment.utc(value).format('DD-MMM-YY')}</span>
                },
                {
                    Header: 'showInReportHeader',
                    accessor: 'isInReport',
                    width: 120,
                    Cell: ({ value }: { value: any }) => (
                        <div style={{ textAlign: 'center', textTransform: 'capitalize' }}>
                            {value ? String(t('yes')) : String(t('no'))}
                        </div>
                    )
                },
                {
                    Header: '',
                    id: 'delete',
                    width: 90,
                    Cell: ({ row }: { row: any }) => {
                        return (
                            <Button
                                variant="default"
                                onClick={() => {
                                    setSelectedPhoto(row.original);
                                    setShowEditPhotoModal(true);
                                }}
                            >
                                {String(t('editPhoto'))}
                            </Button>
                        );
                    }
                }
            ],
            t
        ) as Column<Iphoto>[];
    }, [t]);

    // Use react-table v7 hooks with useFlexLayout
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable<Iphoto>(
        {
            columns,
            data: tableDataWithFallback
        },
        useFlexLayout
    );

    useEffect(() => {
        handleImageFallback();
    }, [tableData, handleImageFallback]);

    const tableClassName = `ReactTable beacon-table photos -highlight ${props.currentTile.color}`;

    if (isMobile) {
        return (
            <PhotoList
                data={tableDataWithFallback}
                t={t}
                installBaseID={installBaseID}
                loading={loading}
                updatePhoto={updatePhoto}
                deletePhoto={deletePhoto}
            />
        );
    }

    return (
        <div>
            <div {...getTableProps()} className={tableClassName}>
                <div className="rt-thead -header">
                    {headerGroups.map(headerGroup => (
                        <div
                            {...headerGroup.getHeaderGroupProps()}
                            key={headerGroup.getHeaderGroupProps().key}
                            className="rt-tr"
                        >
                            {headerGroup.headers.map(column => (
                                <div {...column.getHeaderProps()} key={column.getHeaderProps().key} className="rt-th">
                                    {column.render('Header')}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                <div {...getTableBodyProps()} className="rt-tbody">
                    {rows.map((row, i) => {
                        prepareRow(row);
                        return (
                            <div
                                {...row.getRowProps()}
                                key={row.getRowProps().key}
                                className={`rt-tr ${i % 2 === 1 ? '-odd' : '-even'}`}
                            >
                                {row.cells.map(cell => {
                                    return (
                                        <div {...cell.getCellProps()} key={cell.getCellProps().key} className="rt-td">
                                            {cell.render('Cell')}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>
            {tableDataWithFallback.length === 0 && (
                <div style={{ textAlign: 'center', padding: '20px' }}>{String(t('addPhotoTableNoDataText'))}</div>
            )}

            <CommonModal
                show={showEditPhotoModal}
                onHide={() => setShowEditPhotoModal(false)}
                title="Edit Image"
                className="job-edit"
                container={document.getElementById('two-pane-layout')}
            >
                <EditPhotoForm
                    t={t}
                    loading={loading}
                    installBaseID={installBaseID}
                    formValues={selectedPhoto}
                    toggleModal={() => setShowEditPhotoModal(false)}
                    updatePhoto={props.updatePhoto}
                    deletePhoto={props.deletePhoto}
                />
            </CommonModal>

            <Lightbox
                open={lightboxIsOpen}
                close={closeLightbox}
                index={activePhoto}
                slides={tableDataWithFallback.map(photo => ({
                    src: getLightboxSrc(photo)
                }))}
                carousel={{
                    finite: true,
                    padding: 10
                }}
                styles={{
                    container: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)'
                    }
                }}
            />
        </div>
    );
};
